import type { ReferenceLocationResponse } from 'types/api-responses';

/** Quarter, half and edge positions along each side of a tract. */
const GRID_FRACTIONS = [0, 0.25, 0.5, 0.75, 1];

/** A projected point, `[x, y]` in local metres. */
type XY = [number, number];

/**
 * Convex hull (Andrew's monotone chain), CCW, open ring.
 *
 * @param {Array} pts - Points as `[x, y]`.
 * @return {Array} The hull points.
 */
const convexHull = (pts: XY[]): XY[] => {
  const p = pts.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return p;
  const cross = (o: XY, a: XY, b: XY): number =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: XY[] = [];
  for (const q of p) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], q) <= 0
    ) {
      lower.pop();
    }
    lower.push(q);
  }
  const upper: XY[] = [];
  for (let i = p.length - 1; i >= 0; i--) {
    const q = p[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], q) <= 0
    ) {
      upper.pop();
    }
    upper.push(q);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
};

/**
 * Orientation of the minimum-area bounding rectangle: the angle (radians from
 * +x) of one of its sides. A side of the min-area rectangle is always collinear
 * with a convex-hull edge, so each hull edge is tested.
 *
 * @param {Array} hull - The convex hull points as `[x, y]`.
 * @return {number} The best angle in radians.
 */
const minAreaAngle = (hull: XY[]): number => {
  if (hull.length < 3) return 0;
  let best = 0;
  let bestArea = Infinity;
  for (let i = 0; i < hull.length; i++) {
    const a = hull[i];
    const b = hull[(i + 1) % hull.length];
    const ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
    const c = Math.cos(-ang);
    const s = Math.sin(-ang);
    let minx = Infinity;
    let maxx = -Infinity;
    let miny = Infinity;
    let maxy = -Infinity;
    for (const [x, y] of hull) {
      const rx = x * c - y * s;
      const ry = x * s + y * c;
      if (rx < minx) minx = rx;
      if (rx > maxx) maxx = rx;
      if (ry < miny) miny = ry;
      if (ry > maxy) maxy = ry;
    }
    const area = (maxx - minx) * (maxy - miny);
    if (area < bestArea) {
      bestArea = area;
      best = ang;
    }
  }
  return best;
};

/**
 * Rotate a point about the origin.
 *
 * @param {Array} point - The point `[x, y]`.
 * @param {number} ang - The angle in radians.
 * @return {Array} The rotated point.
 */
const rotateXY = ([x, y]: XY, ang: number): XY => {
  const c = Math.cos(ang);
  const s = Math.sin(ang);
  return [x * c - y * s, x * s + y * c];
};

/**
 * Corners of a ring's minimum-area bounding rectangle, in `[lon, lat]`.
 *
 * Ported verbatim from the mock's `tractMbrCorners`: the section is projected to
 * a local metric frame, its hull's minimum-area rectangle is found, and the four
 * corners are returned. Using the *rotated* min-area rectangle — not an
 * axis-aligned box — is what keeps the 5×5 grid square on tilted survey sections
 * so the dots hug the section instead of spilling outside it.
 *
 * @param {Array} ring - Closed or open ring of `[lon, lat]` positions.
 * @return {Array} Four corners `[lon, lat]`, or null when the ring is unusable.
 */
const mbrCorners = (ring: GeoJSON.Position[]): [number, number][] | null => {
  if (!ring || ring.length < 3) return null;
  const closed =
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1];
  const n = closed ? ring.length - 1 : ring.length;
  if (n < 3) return null;

  let lon0 = 0;
  let lat0 = 0;
  for (let i = 0; i < n; i++) {
    lon0 += ring[i][0];
    lat0 += ring[i][1];
  }
  lon0 /= n;
  lat0 /= n;

  const sLat = 111320;
  const sLon = 111320 * Math.cos((lat0 * Math.PI) / 180);
  const toXY = ([lon, lat]: GeoJSON.Position): XY => [
    (lon - lon0) * sLon,
    (lat - lat0) * sLat,
  ];
  const fromXY = ([x, y]: XY): [number, number] => [
    lon0 + x / sLon,
    lat0 + y / sLat,
  ];

  const projected: XY[] = [];
  for (let i = 0; i < n; i++) projected.push(toXY(ring[i]));
  if (!Number.isFinite(projected[0][0])) return null;

  const ang = minAreaAngle(convexHull(projected));
  let minx = Infinity;
  let maxx = -Infinity;
  let miny = Infinity;
  let maxy = -Infinity;
  for (const point of projected) {
    const [rx, ry] = rotateXY(point, -ang);
    if (rx < minx) minx = rx;
    if (rx > maxx) maxx = rx;
    if (ry < miny) miny = ry;
    if (ry > maxy) maxy = ry;
  }

  // c0 (0,0) c1 (1,0) c2 (1,1) c3 (0,1) — the order the bilinear weights assume,
  // rotated back into the projected frame and unprojected to `[lon, lat]`.
  return (
    [
      [minx, miny],
      [maxx, miny],
      [maxx, maxy],
      [minx, maxy],
    ] as XY[]
  ).map((corner) => fromXY(rotateXY(corner, ang)));
};

/**
 * The compass bearing of a ring's LONG axis, in radians.
 *
 * Taken from the same minimum-area rectangle the quarter grid is built on, so a
 * tilted survey section reports the bearing it actually runs at rather than an
 * axis-aligned 0. Returned in the convention `computeAxisFrame` assumes —
 * 0 = north, increasing clockwise (`u = x·sin(az) + y·cos(az)`) — not the
 * math-frame angle the rectangle is found in.
 *
 * Used for a unit that has no azimuth to inherit: one built from scratch has no
 * selection, so there is no click-derived frame and no `azimuthDeg`, and the
 * cross-section line would otherwise be drawn flat east-west across a section
 * running at 165°.
 *
 * @param {Array} ring - Closed or open ring of `[lon, lat]` positions.
 * @return {number | null} Bearing in radians, or null for an unusable ring.
 */
export const ringAxisAzimuthRad = (ring: GeoJSON.Position[]): number | null => {
  const corners = mbrCorners(ring);
  if (!corners) return null;

  const [c0, c1, c2] = corners;
  // Corner order is c0(min,min) c1(max,min) c2(max,max), so c0→c1 and c1→c2 are
  // the rectangle's two sides.
  const side = (a: [number, number], b: [number, number]) => {
    const midLatRad = (((a[1] + b[1]) / 2) * Math.PI) / 180;
    const east = (b[0] - a[0]) * 111320 * Math.cos(midLatRad);
    const north = (b[1] - a[1]) * 111320;
    return { east, north, length: Math.hypot(east, north) };
  };

  const a = side(c0, c1);
  const b = side(c1, c2);
  const long = a.length >= b.length ? a : b;
  if (!Number.isFinite(long.length) || long.length === 0) return null;

  return Math.atan2(long.east, long.north);
};

/**
 * Pull every outer ring out of a geometry, ignoring holes.
 *
 * @param {object} geometry - Polygon or MultiPolygon.
 * @return {Array} The outer rings.
 */
const outerRings = (geometry: GeoJSON.Geometry): GeoJSON.Position[][] => {
  if (geometry.type === 'Polygon') return [geometry.coordinates[0]];
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.map((polygon) => polygon[0]);
  }
  return [];
};

/**
 * Quarter-grid points for one tract — a 5×5 grid covering the section: each side
 * of its minimum-area bounding rectangle split into four, the interior filled by
 * bilinearly interpolating the corners.
 *
 * Ported from the mock's `quarterGridForTract`.
 *
 * @param {GeoJSON.Geometry} geometry - The tract's geometry.
 * @return {Array} Grid points as `[lon, lat]`.
 */
export const quarterGridForTract = (
  geometry: GeoJSON.Geometry
): GeoJSON.Position[] => {
  const out: GeoJSON.Position[] = [];
  for (const ring of outerRings(geometry)) {
    const corners = mbrCorners(ring);
    if (!corners) continue;
    const [c0, c1, c2, c3] = corners;
    for (const v of GRID_FRACTIONS) {
      for (const u of GRID_FRACTIONS) {
        const w0 = (1 - u) * (1 - v);
        const w1 = u * (1 - v);
        const w2 = u * v;
        const w3 = (1 - u) * v;
        out.push([
          w0 * c0[0] + w1 * c1[0] + w2 * c2[0] + w3 * c3[0],
          w0 * c0[1] + w1 * c1[1] + w2 * c2[1] + w3 * c3[1],
        ]);
      }
    }
  }
  return out;
};

/**
 * De-duplicate points that round to the same 6-dp lon/lat.
 *
 * @param {Array} points - Points as `[lon, lat]`.
 * @return {Array} The points, with near-identical ones collapsed.
 */
const dedupePoints = (points: GeoJSON.Position[]): GeoJSON.Position[] => {
  const seen = new Set<string>();
  const out: GeoJSON.Position[] = [];
  for (const point of points) {
    // Rounded to collapse near-identical corners of adjoining tracts. Not
    // display text, so the repo's formatNumber rule does not apply — Number()
    // makes that explicit.
    const key = `${Number(point[0].toFixed(6))},${Number(point[1].toFixed(6))}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(point);
  }
  return out;
};

/** A survey row far enough along to contribute a tract. */
interface TractRow {
  location?: ReferenceLocationResponse;
}

/**
 * Quarter-grid points for every tract the survey rows name, de-duplicated.
 * Yields nothing for rows whose location carries no geometry.
 *
 * @param {Array} rows - The survey rows.
 * @return {Array} Grid points as `[lon, lat]`.
 */
export const quarterGridForRows = (
  rows: readonly TractRow[]
): GeoJSON.Position[] => {
  const points: GeoJSON.Position[] = [];
  for (const row of rows) {
    const geometry = row.location?.geometry as GeoJSON.Geometry | undefined;
    if (!geometry) continue;
    points.push(...quarterGridForTract(geometry));
  }
  return dedupePoints(points);
};

/**
 * The geometries a boundary is built from: every tract the rows name, plus the
 * development area's own footprint when there is one.
 *
 * @param {Array} rows - The survey rows.
 * @param {Array | null} unitRing - The development area's ring, or null.
 * @return {Array} One geometry per shape.
 */
const boundaryGeometries = (
  rows: readonly TractRow[],
  unitRing: GeoJSON.Position[] | null
): GeoJSON.Geometry[] => {
  const geometries: GeoJSON.Geometry[] = [];
  for (const row of rows) {
    const geometry = row.location?.geometry as GeoJSON.Geometry | undefined;
    if (geometry) geometries.push(geometry);
  }
  if (unitRing && unitRing.length >= 3) {
    geometries.push({ type: 'Polygon', coordinates: [unitRing] });
  }
  return geometries;
};

/**
 * Quarter-grid points for the whole boundary — every named tract AND the
 * development area itself — de-duplicated into one set.
 *
 * Gridding the unit as well as its surveys is what lets a vertex snap to the
 * unit's own quarter positions, and de-duplicating into ONE set is what makes
 * welding possible: two vertices dragged to the same dot get identical
 * coordinates, so the pieces they belong to dissolve on merge.
 *
 * These are convenience positions, not survey truth — the grid is derived from
 * each shape's minimum-area bounding rectangle, so a dot need not sit on the real
 * edge. Callers offer {@link outlineVerticesFor} ahead of these so a true corner
 * wins whenever one is in range.
 *
 * @param {Array} rows - The survey rows.
 * @param {Array | null} unitRing - The development area's ring, or null.
 * @return {Array} Grid points as `[lon, lat]`.
 */
export const quarterGridForUnit = (
  rows: readonly TractRow[],
  unitRing: GeoJSON.Position[] | null
): GeoJSON.Position[] => {
  const points: GeoJSON.Position[] = [];
  for (const geometry of boundaryGeometries(rows, unitRing)) {
    points.push(...quarterGridForTract(geometry));
  }
  return dedupePoints(points);
};

/**
 * The TRUE outer rings of the tracts and the development area.
 *
 * The rings themselves, not just their corners, so a dragged vertex can land on
 * an EDGE of the development area rather than only on one of its ends. Most of a
 * unit's outline is edge, so corners alone cannot make the boundary follow it.
 *
 * @param {Array} rows - The survey rows.
 * @param {Array | null} unitRing - The development area's ring, or null.
 * @return {Array} One closed ring per shape.
 */
export const outlineRingsFor = (
  rows: readonly TractRow[],
  unitRing: GeoJSON.Position[] | null
): GeoJSON.Position[][] => {
  const rings: GeoJSON.Position[][] = [];
  for (const geometry of boundaryGeometries(rows, unitRing)) {
    rings.push(...outerRings(geometry));
  }
  return rings;
};

/**
 * The TRUE outer-ring vertices of the tracts and the development area.
 *
 * Real survey corners, straight from `reference_locations` and the unit's own
 * polygon — not the bounding-rectangle approximations the quarter grid is built
 * from, and never anything read back out of a map tile. Offered as the preferred
 * snap targets so a dragged vertex lands exactly on a surveyed corner, which is
 * the only way two pieces can share an edge precisely enough to merge.
 *
 * @param {Array} rows - The survey rows.
 * @param {Array | null} unitRing - The development area's ring, or null.
 * @return {Array} Vertices as `[lon, lat]`.
 */
export const outlineVerticesFor = (
  rows: readonly TractRow[],
  unitRing: GeoJSON.Position[] | null
): GeoJSON.Position[] => {
  const points: GeoJSON.Position[] = [];
  for (const geometry of boundaryGeometries(rows, unitRing)) {
    for (const ring of outerRings(geometry)) points.push(...ring);
  }
  return dedupePoints(points);
};
